import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Agent from 'models/agent';
import { CHANNEL_REPLY_TEXT } from 'components/lib/channel_action_text';
import ConversationItem, { getOutgoingCommunicationStatus } from 'models/conversation_item';
import ConversationItemType from 'models/conversation_item_type';
import connect from 'components/lib/connect';
import { getBrandIconByName } from 'components/common/custom_channels';
import { iconClassNameForItem } from './metadata_icon_classes';
import ItemPrepositionalPhrases from './item_prepositional_phrases';
import Metadata from './metadata';
import TranslationLanguages from 'models/languages/translation_languages';
import { typeReflect } from 'models/conversation_item/content_from_js';

export function AgentMetadata({
  agent,
  currentAgentId,
  customChannels,
  hasError,
  icon,
  iconClass,
  isCollapsed,
  item,
  object,
  prepositionalPhrases,
  status,
  subject,
  text,
  tooltip,
}) {
  const { t } = useTranslation();
  hasError = hasError != null ? hasError : item.content.hasErrorStatus && item.content.hasErrorStatus();
  iconClass = iconClass != null ? iconClass : iconClassNameForItem(item);
  status = status != null ? status : getOutgoingCommunicationStatus(item);
  const rawSubject = subject != null ? subject : getAgentName(agent, currentAgentId);
  subject = typeof rawSubject === 'string' ? t(rawSubject) : rawSubject;

  const contentType = typeReflect.instanceToType(item.content);

  let repliedViaChannel;
  const isCustomChannel = ConversationItemType.CUSTOM_CHANNEL_MESSAGE === contentType;
  if (isCustomChannel) {
    const { customChannelId } = item.content;
    const customChannel = _.find(customChannels, { id: customChannelId });

    repliedViaChannel = customChannel?.name;
    icon = <>{getBrandIconByName(customChannel?.icon, { letter: customChannel?.name, stroke: true }).icon}</>;
  } else {
    repliedViaChannel = CHANNEL_REPLY_TEXT[contentType];
  }

  let metaText;
  if (text != null) {
    metaText = typeof text === 'string' ? t(text) : text;
  } else {
    const channel =
      !isCustomChannel && typeof repliedViaChannel === 'string' ? t(repliedViaChannel) : repliedViaChannel;
    metaText = t('replied via {{channel}}', { channel });
  }
  let language = item.translation ? TranslationLanguages[item.translation.parentLanguage] : undefined;
  return (
    <Metadata
      hasError={hasError}
      icon={icon}
      iconClass={iconClass}
      isCollapsed={isCollapsed}
      isInbound={false}
      language={language}
      object={object}
      prepositionalPhrases={prepositionalPhrases || <ItemPrepositionalPhrases item={item} />}
      status={status}
      subject={subject}
      text={metaText}
      timestamp={item.timestamp}
      tooltip={tooltip}
    />
  );
}

export function getAgentName(agent, currentAgentId) {
  if (!agent) {
    return 'An agent';
  } else if (currentAgentId === agent.id) {
    return 'You';
  }
  return agent.name;
}

AgentMetadata.propTypes = {
  agent: PropTypes.instanceOf(Agent),
  currentAgentId: PropTypes.string.isRequired,
  hasError: PropTypes.bool,
  icon: PropTypes.node,
  iconClass: PropTypes.string,
  isCollapsed: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  object: PropTypes.string,
  prepositionalPhrases: PropTypes.node,
  status: PropTypes.string,
  subject: PropTypes.node,
  text: PropTypes.any,
  tooltip: PropTypes.string,
};

function mapStateToProps({ getProvider }, props) {
  const currentAgentId = getProvider('currentAgent').get().id;
  const agent = getProvider('agents').findBy({ id: _.get(props, 'item.initiator.id') });
  const customChannels = getProvider('customChannels').findAll();

  return {
    ...props,
    agent,
    currentAgentId,
    customChannels,
  };
}

const AgentMetadataContainer = connect(mapStateToProps)(AgentMetadata);
AgentMetadataContainer.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

export default AgentMetadataContainer;
